import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Spinner, Alert, Table } from 'reactstrap'
import api from '../../../services/api'
import AddRuleModal from './AddRuleModal'

const RulesView = () => {
  const { companyId } = useParams()

  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.get(`/company/${companyId}/email-sync/rules`)
      if (res.ok) {
        setRules(await res.json())
      } else {
        setError('Failed to load rules')
      }
    } catch {
      setError('Failed to load rules')
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => { load() }, [load])

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this rule? This will not clear values already pushed to CW.')) return
    try {
      const res = await api.deleteCall(`/company/${companyId}/email-sync/rules/${ruleId}`)
      if (res.ok) await load()
      else setError('Failed to delete rule')
    } catch {
      setError('Failed to delete rule')
    }
  }

  if (loading) return <div className="text-center p-4"><Spinner color="primary" /></div>

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="mb-0">
          Rules push the user's primary SMTP on <em>alias domain</em> into the chosen CW slot
          for users whose UPN ends in <em>UPN domain</em>. Existing CW values are never overwritten;
          if the preferred slot is taken, sync falls back to the other email slot.
        </p>
        <Button color="primary" onClick={() => setModalOpen(true)}>+ Add Rule</Button>
      </div>

      {rules.length === 0 ? (
        <Alert color="info">No rules configured. Click "Add Rule" to create one.</Alert>
      ) : (
        <Table striped responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>UPN domain</th>
              <th>Alias domain</th>
              <th>Preferred CW slot</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rules.map(r => {
              const isMismatch = r.ruleType === 'PrimarySmtpMismatch'
              return (
                <tr key={r.id}>
                  <td className="align-middle">
                    {isMismatch ? 'Primary SMTP mismatch' : 'Domain'}
                  </td>
                  <td className="align-middle">
                    {isMismatch ? <span className="text-muted">any</span> : r.upnDomain}
                  </td>
                  <td className="align-middle">
                    {isMismatch
                      ? <span className="text-muted">user's primary SMTP</span>
                      : r.aliasDomain}
                  </td>
                  <td className="align-middle">
                    {r.preferredCommTypeDescription || `id ${r.preferredCommTypeId}`}
                  </td>
                  <td className="align-middle">{new Date(r.created).toLocaleString()}</td>
                  <td className="align-middle">
                    <Button color="link" size="sm" onClick={() => handleDelete(r.id)}>Delete</Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {modalOpen && (
        <AddRuleModal
          companyId={companyId}
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          onCreated={() => { setModalOpen(false); load() }}
        />
      )}
    </div>
  )
}

export default RulesView
